import React, {
  useRef,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import classes from "./LoginForm.module.css";
import Grid from "@mui/material/Grid";
import images from "../assets/images";
import Checkbox from "../framework/Checkbox";
import TextInput from "../framework/TextInput";
import Button from "../framework/Button";
import Shortcut from "../framework/Shortcut";
import GlobalContext from "../framework/GlobalContext";
import { Link, useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import { login } from "../services/login-services";
import { displayToast } from "../framework/toast/toastUtils";
import { getConfig } from "../services/configuration-service";
import { useDispatch, useSelector } from "react-redux";

const LoginForm = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [retailerCode, setRetailerCode] = useState("");
  const [tncAccepted, setTncAccepted] = useState(true);
  const { setCurrentComponent } = useContext(GlobalContext);

  const usernameRef = useRef();
  const passwordRef = useRef();
  const retailerCodeRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { appLogo } = images;

  const dispatchConfigState = useCallback(
    (newState) => {
      dispatch({
        type: "set_config_data",
        payload: newState,
      });
    },
    [dispatch]
  );

  const getConfigDetails = useCallback(() => {
    getConfig().then((response) => {
      if (response) {
        dispatchConfigState(response);
      }
    });
  }, [dispatchConfigState]);

  const isPasswordValid = (password) => {
    return password.match(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)([a-zA-Z\d])(?=.*[!@#$%^&*(),.?":{}|<>_]).{7,}$/gm
    );
  };

  const loginSubmitHandler = useCallback(
    (e, isLoginButton) => {
      if (
        isEmpty(username) ||
        isEmpty(password) ||
        isEmpty(retailerCode) ||
        !tncAccepted
      ) {
        if (isLoginButton)
          setTimeout(
            () => usernameRef.current.focus({ preventScroll: true }),
            100
          );
        return;
      }
      login({ username, password, company: retailerCode }).then((response) => {
        if (response.status !== "success") {
          displayToast({
            type: "error",
            title: response?.result || "Invalid credentials. Please try again.",
          });
          setTimeout(
            () => usernameRef.current.focus({ preventScroll: true }),
            100
          );
          return;
        }
        // getConfigDetails();
        sessionStorage.setItem("token", response.result.token);
        navigate("/home");
      });
    },
    [navigate, password, retailerCode, tncAccepted, username]
  );
  // const loginSubmitHandler = (event) => {
  //   const formdata = new FormData();
  //   formdata.append("username", usernameRef.current.value);
  //   formdata.append(
  //     "password",
  //     cryptoJs.AES.encrypt(
  //       passwordRef.current.value,
  //       "eyJuYW1lIjoiTWlz"
  //     ).toString()
  //   );
  //   formdata.append("company", retailerCodeRef.current.value);
  //   login(formdata)
  //     .then((data) => {
  //       navigate("/home");
  //     })
  //     .catch((reason) => console.error(reason));
  // };
  const loginListener = useCallback(
    (e) => {
      const key = e.key?.toLowerCase();
      if (key === "enter") {
        loginSubmitHandler();
      }
    },
    [loginSubmitHandler]
  );
  useEffect(() => {
    document.addEventListener("keyup", loginListener, false);
    return () => {
      document.removeEventListener("keyup", loginListener, false);
    };
  }, [loginListener]);
  useEffect(() => {
    if (sessionStorage.getItem("token")) navigate("/home");
  }, [navigate]);

  useEffect(() => {
    return () => {
      setCurrentComponent(null);
    };
  }, [setCurrentComponent]);

  return (
    <div className={classes["login-form-container"]}>
      <Grid
        container
        rowSpacing={5}
        columns={12}
        sx={{ width: "80%", textAlign: "center" }}
      >
        <Grid item xs={12}>
          <img src={appLogo} alt="PharmacyPro" />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            id="login_username"
            ref={usernameRef}
            variant="standard"
            placeholder="Employee Id"
            onFocus={(e) => setCurrentComponent("login_username")}
            onChange={(e) => setUsername(e.target.value)}
            inputProps={{
              "data-markers": JSON.stringify({
                altUp: "",
                down: "login_password",
                left: "",
                right: "login_password",
              }),
            }}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            id="login_password"
            ref={passwordRef}
            type="password"
            placeholder="Password"
            variant="standard"
            onFocus={(e) => setCurrentComponent("login_password")}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "login_username",
                down: "login_retailer_code",
                left: "login_username",
                right: "login_retailer_code",
              }),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            id="login_retailer_code"
            ref={retailerCodeRef}
            placeholder="Company Code"
            variant="standard"
            onFocus={(e) => setCurrentComponent("login_retailer_code")}
            onChange={(e) => setRetailerCode(e.target?.value?.toUpperCase())}
            inputProps={{
              style: { textTransform: "uppercase" },
              "data-markers": JSON.stringify({
                up: "login_password",
                down: tncAccepted ? "login_signin_button" : "login_agreeTerms",
                left: "login_password",
                right: tncAccepted ? "login_signin_button" : "login_agreeTerms",
              }),
            }}
          />
        </Grid>
        <Grid
          item
          sx={{
            textAlign: "left",
          }}
          xs={12}
        >
          <Checkbox
            id="login_agreeTerms"
            name="agreeTerms"
            checked={tncAccepted}
            onFocus={(e) => setCurrentComponent("login_agreeTerms")}
            onChange={(e) => setTncAccepted(e.target.checked)}
            label={
              <p
                style={{
                  fontWeight: 300,
                }}
              >
                <span>By clicking Login, you agree to the </span>
                <Link className={classes["link-text"]} to={"#"}>
                  Terms of Use
                </Link>
                <span> of our software</span>
              </p>
            }
            inputProps={{
              "data-markers": JSON.stringify({
                up: "login_retailer_code",
                down: "login_signin_button",
                left: "login_retailer_code",
                right: "login_signin_button",
              }),
            }}
          />
        </Grid>
        <Grid item>
          <Button
            id="login_signin_button"
            disabled={
              isEmpty(username) ||
              isEmpty(password) ||
              isEmpty(retailerCode) ||
              !tncAccepted
            }
            onFocus={(e) => setCurrentComponent("login_signin_button")}
            onClick={(e) => loginSubmitHandler(e, true)}
            label={"Login"}
            color="violet"
            markers={JSON.stringify({
              up: tncAccepted ? "login_retailer_code" : "login_agreeTerms",
              left: tncAccepted ? "login_retailer_code" : "login_agreeTerms",
              right: "login_signin_button",
            })}
            endIcon={<Shortcut keys={["Enter"]} />}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default LoginForm;
